/**
 * EffectiveRateDisplay - Shows the effective electric rate for an entity
 * Displays rate details including utility provider, rate source, and period
 */

import { Zap, Building2, Users, Folder, Globe } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { cn } from '@/lib/utils';
import type { EffectiveRateResponse, RateSource } from '@/types/rates';
import { formatRatePerKwh, formatEffectivePeriod } from '@/types/rates';

interface EffectiveRateDisplayProps {
  rate: EffectiveRateResponse | null;
  isLoading?: boolean;
  error?: string | null;
  /** Display variant */
  variant?: 'compact' | 'detailed' | 'inline';
  className?: string;
}

function getRateSourceIcon(source: RateSource) {
  switch (source) {
    case 'unit':
      return Zap;
    case 'site':
      return Folder;
    case 'group':
      return Users;
    case 'company':
      return Building2;
    case 'system':
    case 'fallback':
      return Globe;
    default:
      return Globe;
  }
}

function getRateSourceLabel(source: RateSource): string {
  switch (source) {
    case 'unit':
      return 'Unit Override';
    case 'site':
      return 'Site Rate';
    case 'group':
      return 'Group Rate';
    case 'company':
      return 'Company Default';
    case 'system':
      return 'System Default';
    case 'fallback':
      return 'Fallback Rate';
    default:
      return 'Inherited';
  }
}

function getRateSourceColor(source: RateSource): string {
  switch (source) {
    case 'unit':
      return 'bg-[#8FB560]/20 text-[#8FB560] border border-[#8FB560]/30';
    case 'site':
      return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
    case 'group':
      return 'bg-purple-500/20 text-purple-400 border border-purple-500/30';
    case 'company':
      return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
    case 'system':
    case 'fallback':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

export function EffectiveRateDisplay({
  rate,
  isLoading = false,
  error = null,
  variant = 'compact',
  className,
}: EffectiveRateDisplayProps) {
  if (isLoading) {
    return (
      <div className={cn('animate-pulse', className)}>
        <div className="h-4 w-32 bg-muted rounded" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('text-sm text-destructive', className)}>
        {error}
      </div>
    );
  }

  // Allow fallback rates (rateId = null but rateName exists) to display
  if (!rate || (!rate.rateId && !rate.rateName)) {
    return (
      <div className={cn('text-sm text-muted-foreground', className)}>
        No rate assigned
      </div>
    );
  }

  const SourceIcon = getRateSourceIcon(rate.rateSource);

  // Inline variant - single line for headers/lists
  if (variant === 'inline') {
    return (
      <div className={cn('flex items-center gap-2 text-sm', className)}>
        <Zap className="h-3.5 w-3.5 text-[#8FB560]" />
        <span className="font-medium">{rate.rateName}</span>
        {rate.utilityProvider && (
          <span className="text-muted-foreground">({rate.utilityProvider})</span>
        )}
        <Badge className={cn('text-[10px] px-1.5', getRateSourceColor(rate.rateSource))}>
          {getRateSourceLabel(rate.rateSource)}
        </Badge>
      </div>
    );
  }

  // Compact variant - for cards/sidebars
  if (variant === 'compact') {
    return (
      <div className={cn('rounded-lg border border-border p-3 space-y-2', className)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-[#8FB560]" />
            <span className="font-semibold text-sm">{rate.rateName}</span>
          </div>
          <InfoTooltip
            content={
              <div className="space-y-1 text-xs">
                <p><strong>Import Rate:</strong> {formatRatePerKwh(rate.gridImportRate)}</p>
                <p><strong>Sell Rate:</strong> {formatRatePerKwh(rate.gridSellRate)}</p>
                {rate.dieselCostPerGallon > 0 && (
                  <p><strong>Diesel Cost:</strong> ${rate.dieselCostPerGallon.toFixed(2)}/gal</p>
                )}
                {rate.effectiveFrom && (
                  <p><strong>Period:</strong> {formatEffectivePeriod(rate.effectiveFrom, rate.effectiveTo)}</p>
                )}
              </div>
            }
          />
        </div>

        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <SourceIcon className="h-3 w-3" />
            <span>{rate.sourceName}</span>
          </div>
          <Badge className={cn('text-[10px] px-1.5', getRateSourceColor(rate.rateSource))}>
            {getRateSourceLabel(rate.rateSource)}
          </Badge>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-border/50">
          <div className="text-center">
            <div className="text-[10px] text-muted-foreground uppercase">Import</div>
            <div className="font-mono text-sm font-semibold">{formatRatePerKwh(rate.gridImportRate)}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] text-muted-foreground uppercase">Sell</div>
            <div className="font-mono text-sm font-semibold">{formatRatePerKwh(rate.gridSellRate)}</div>
          </div>
        </div>

        {rate.utilityProvider && (
          <div className="text-xs text-muted-foreground text-center pt-1 border-t border-border/50">
            {rate.utilityProvider}
            {rate.nemVersion && ` • ${rate.nemVersion}`}
            {rate.stateProvince && ` • ${rate.stateProvince}`}
          </div>
        )}
      </div>
    );
  }

  // Detailed variant - for full displays
  return (
    <div className={cn('rounded-lg border border-border overflow-hidden', className)}>
      {/* Header */}
      <div className="bg-[#8FB560]/10 border-b border-[#8FB560]/20 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-[#8FB560]" />
            <span className="font-semibold">{rate.rateName}</span>
          </div>
          <Badge className={cn('text-xs', getRateSourceColor(rate.rateSource))}>
            {getRateSourceLabel(rate.rateSource)}
          </Badge>
        </div>
        {rate.description && (
          <p className="text-sm text-muted-foreground mt-1">{rate.description}</p>
        )}
      </div>

      {/* Rate Details */}
      <div className="p-4 space-y-4">
        {/* Rates Grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-2 rounded bg-muted/50">
            <div className="text-xs text-muted-foreground uppercase mb-1">Grid Import</div>
            <div className="font-mono text-lg font-bold">{formatRatePerKwh(rate.gridImportRate)}</div>
          </div>
          <div className="text-center p-2 rounded bg-muted/50">
            <div className="text-xs text-muted-foreground uppercase mb-1">Grid Sell</div>
            <div className="font-mono text-lg font-bold">{formatRatePerKwh(rate.gridSellRate)}</div>
          </div>
          <div className="text-center p-2 rounded bg-muted/50">
            <div className="text-xs text-muted-foreground uppercase mb-1">Diesel</div>
            <div className="font-mono text-lg font-bold">${rate.dieselCostPerGallon.toFixed(2)}</div>
          </div>
        </div>

        {/* Source & Provider Info */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs text-muted-foreground uppercase mb-1">Rate Source</div>
            <div className="flex items-center gap-1.5">
              <SourceIcon className="h-4 w-4" />
              <span>{rate.sourceName}</span>
            </div>
          </div>
          {rate.utilityProvider && (
            <div>
              <div className="text-xs text-muted-foreground uppercase mb-1">Utility Provider</div>
              <div>{rate.utilityProvider}</div>
            </div>
          )}
          {rate.nemVersion && (
            <div>
              <div className="text-xs text-muted-foreground uppercase mb-1">NEM Version</div>
              <div>{rate.nemVersion}</div>
            </div>
          )}
          {rate.stateProvince && (
            <div>
              <div className="text-xs text-muted-foreground uppercase mb-1">State/Province</div>
              <div>{rate.stateProvince}</div>
            </div>
          )}
        </div>

        {/* Effective Period */}
        {rate.effectiveFrom && (
          <div className="pt-3 border-t border-border">
            <div className="text-xs text-muted-foreground uppercase mb-1">Effective Period</div>
            <div className="text-sm">{formatEffectivePeriod(rate.effectiveFrom, rate.effectiveTo)}</div>
          </div>
        )}
      </div>
    </div>
  );
}
